import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { 
  CheckCircle2, 
  Coins, 
  Loader2, 
  Sparkles, 
  Trophy, 
  CalendarDays, 
  Calendar, 
  Flame, 
  Zap 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const CATEGORIES = [
  { key: 'DAILY', labelKey: 'daily', defaultLabel: 'Daily', icon: Zap },
  { key: 'WEEKLY', labelKey: 'weekly', defaultLabel: 'Weekly', icon: CalendarDays },
  { key: 'MONTHLY', labelKey: 'monthly', defaultLabel: 'Monthly', icon: Calendar },
  { key: 'ONETIME', labelKey: 'oneTime', defaultLabel: 'One-Time', icon: Flame },
];

function TasksTab({ user, setUser }) {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [activeTab, setActiveTab] = useState('DAILY');

  const fetchTasks = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tasks/${user.id}`);
      if (res.data?.success) {
        setTasks(res.data.tasks);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [user?.id]);

  const handleClaim = async (taskId) => {
    if (!user?.id || claimingId) return;
    setClaimingId(taskId);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/tasks/claim`, {
        userId: user.id,
        taskId: taskId,
      });

      if (res.data?.success) {
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId ? { ...t, is_completed: true } : t
          )
        );

        if (setUser && res.data.newCoins !== undefined) {
          setUser((prev) => ({
            ...prev,
            coins: res.data.newCoins,
          }));
        }
      }
    } catch (err) {
      console.error('Error claiming task reward:', err);
    } finally {
      setClaimingId(null);
    }
  };

  const TASK_KEY_MAP = {
    'Daily Check-in': 'task_daily_checkin',
    'First Trade of the Day': 'task_first_trade',
    'Active Trader (5 Trades)': 'task_active_trader',
    'Trading Master (10 Trades)': 'task_trading_master',
    'Coin Round Explorer': 'task_coin_explorer',
    'Daily Victor': 'task_daily_victor',
    'Weekly Volume (50 Trades)': 'task_weekly_volume',
    'Streak Builder (3 Streak)': 'task_streak_builder',
    'Asset Explorer': 'task_asset_explorer',
    'High Roller': 'task_high_roller',
    'Monthly Volume (200 Trades)': 'task_monthly_volume',
    'Monthly Champion (20 Wins)': 'task_monthly_champion',
    'Monthly Referral Challenge': 'task_monthly_referral',
    'Dedicated Trader (15 Days)': 'task_dedicated_trader',
    'First Victory': 'task_first_victory',
    'First Friend Invited': 'task_first_friend',
    'Squad Builder (5 Friends)': 'task_squad_builder',
    'Network Master (20 Friends)': 'task_network_master',
    'Hot Streak (5 Wins)': 'task_hot_streak',
    'Unstoppable (10 Wins)': 'task_unstoppable',
    'Centurion (100 Trades)': 'task_centurion',
    'Join Telegram Channel': 'task_join_telegram',
    'Follow on X (Twitter)': 'task_follow_x',
  };
  // تابع تبدیل عنوان دیتابیس به کلید ترجمه i18n
  const getTranslatedTask = (task) => {
    // 1. اگر از بک‌اند task_key آمد از همان استفاده کن
    // 2. در غیر این صورت از نقشه تطبیق بر اساس عنوان دیتابیس استفاده کن
    let baseKey = task.task_key || TASK_KEY_MAP[task.title];

    // 3. Fallback هوشمند برای حالت‌هایی که عنوان جدیدی اضافه شود و در Map نباشد
    if (!baseKey && task.title) {
      const formattedTitle = task.title
        .toLowerCase()
        .replace(/\s*\(.*?\)\s*/g, '') // حذف محتوای داخل پرانتز مثل (5 Trades)
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      baseKey = `task_${formattedTitle}`;
    }

    const titleKey = `${baseKey}_title`;
    const descKey = `${baseKey}_desc`;

    const translatedTitle = t(titleKey);
    const translatedDesc = t(descKey);

    return {
      title: translatedTitle !== titleKey ? translatedTitle : task.title,
      description: translatedDesc !== descKey ? translatedDesc : task.description,
    };
  };

  // فیلتر کردن تسک‌ها بر اساس تب فعال
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => task.task_type === activeTab);
  }, [tasks, activeTab]);

  // محاسبه تعداد تسک‌های آماده Claim در هر دسته برای نشان دادن Badge
  const claimableCounts = useMemo(() => {
    const counts = {};
    tasks.forEach((task) => {
      const progress = Math.min(task.progress || 0, task.target_count || 1);
      const isReady = progress >= (task.target_count || 1) && !task.is_completed;
      if (isReady) {
        counts[task.task_type] = (counts[task.task_type] || 0) + 1;
      }
    });
    return counts;
  }, [tasks]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-white/50 gap-3">
        <Loader2 className="animate-spin text-purple-500" size={32} />
        <span className="text-sm">{t('loading') || 'Loading tasks...'}</span>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 max-w-md mx-auto text-white space-y-4 animate-fadeIn">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-purple-900/60 to-indigo-900/60 border border-purple-500/20 p-5 shadow-xl">
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-black text-white flex items-center gap-2">
              <Trophy className="text-amber-400" size={24} />
              {t('tasksAndRewards') || 'Tasks & Rewards'}
            </h1>
            <p className="text-xs text-purple-200/70 mt-1">
              {t('completeTasksDescription') || 'Complete tasks to earn extra COINs!'}
            </p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 rounded-2xl flex items-center gap-1.5 shrink-0">
            <Coins className="text-amber-400" size={18} />
            <span className="font-bold font-mono text-amber-300 text-sm">
              {user?.coins || 0}
            </span>
          </div>
        </div>
        <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl" />
      </div>

      {/* Category Tabs */}
      <div className="grid grid-cols-4 gap-1.5 bg-[#120F1D]/80 p-1.5 rounded-2xl border border-white/5 backdrop-blur-md">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const isActive = activeTab === cat.key;
          const claimableNum = claimableCounts[cat.key] || 0;

          return (
            <button
              key={cat.key}
              onClick={() => setActiveTab(cat.key)}
              className={`relative flex flex-col items-center justify-center py-2 px-1 rounded-xl font-bold text-xs transition-all ${
                isActive
                  ? 'bg-purple-600/80 text-white shadow-lg shadow-purple-600/30 border border-purple-400/30'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <Icon size={16} className={`mb-1 ${isActive ? 'text-amber-300' : 'text-white/40'}`} />
              <span className="text-[11px] truncate w-full text-center">
                {t(cat.labelKey) || cat.defaultLabel}
              </span>

              {/* Red/Amber Dot Badge for Claimable Rewards */}
              {claimableNum > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-extrabold text-black ring-2 ring-[#120F1D]">
                  {claimableNum}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Task List */}
      <div className="space-y-3">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-12 bg-[#120F1D]/60 rounded-2xl border border-white/5 text-white/40 text-sm flex flex-col items-center gap-2">
            <Sparkles size={28} className="text-white/20" />
            <span>{t('noTasksAvailable') || 'No tasks available in this section.'}</span>
          </div>
        ) : (
          filteredTasks.map((task) => {
            const progress = Math.min(task.progress || 0, task.target_count || 1);
            const target = task.target_count || 1;
            const progressPercent = Math.min((progress / target) * 100, 100);
            const isReadyToClaim = progress >= target && !task.is_completed;

            // دریافت عنوان و توضیحات ترجمه‌شده
            const { title, description } = getTranslatedTask(task);

            return (
              <div
                key={task.id}
                className={`p-4 rounded-2xl border transition-all ${
                  task.is_completed
                    ? 'bg-[#120F1D]/40 border-white/5 opacity-60'
                    : isReadyToClaim
                    ? 'bg-[#1B152B] border-amber-500/40 shadow-lg shadow-amber-500/10'
                    : 'bg-[#161224] border-white/10'
                }`}
              >
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div>
                    <h3 className="font-bold text-sm text-white">{title}</h3>
                    {description && (
                      <p className="text-xs text-white/50 mt-0.5">{description}</p>
                    )}
                  </div>

                  {/* Reward Badge */}
                  <div className="flex items-center gap-1 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-xl shrink-0">
                    <Coins size={14} className="text-amber-400" />
                    <span className="text-xs font-black text-amber-400 font-mono">
                      +{task.reward_coins}
                    </span>
                  </div>
                </div>

                {/* Progress Bar & Button */}
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-[10px] text-white/50 mb-1 font-mono">
                      <span>{t('progress') || 'Progress'}</span>
                      <span>
                        {progress} / {target}
                      </span>
                    </div>
                    <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden border border-white/5">
                      <div
                        className={`h-full transition-all duration-300 ${
                          task.is_completed
                            ? 'bg-emerald-500'
                            : isReadyToClaim
                            ? 'bg-amber-400'
                            : 'bg-purple-500'
                        }`}
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="shrink-0">
                    {task.is_completed ? (
                      <div className="flex items-center gap-1 text-emerald-400 text-xs font-bold px-3 py-1.5 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                        <CheckCircle2 size={16} />
                        <span>{t('claimed') || 'Claimed'}</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleClaim(task.id)}
                        disabled={!isReadyToClaim || claimingId === task.id}
                        className={`px-4 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all ${
                          isReadyToClaim
                            ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:brightness-110 shadow-lg shadow-amber-500/20'
                            : 'bg-white/10 text-white/30 cursor-not-allowed'
                        }`}
                      >
                        {claimingId === task.id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : isReadyToClaim ? (
                          <>
                            <Sparkles size={14} />
                            <span>{t('claim') || 'Claim'}</span>
                          </>
                        ) : (
                          <span>{t('inProgress') || 'In Progress'}</span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default TasksTab;